import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PlusCircle, Pencil, Trash2, ImagePlus, Megaphone } from "lucide-react";
import api, { apiError } from "../lib/api";

const SLOTS = [
  { id: "top", label: "हीरो के नीचे" },
  { id: "middle", label: "पेज के बीच में" },
  { id: "bottom", label: "फुटर के ऊपर" },
];

const EMPTY = { title: "", slot: "top", image_url: "", link: "", enabled: true };

const inputCls =
  "w-full border border-ink/25 bg-white px-3 py-2 text-sm outline-none focus:border-deepgreen transition-colors";

export default function AdminAds() {
  const [ads, setAds] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/ads").then((r) => setAds(r.data.ads)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const openForm = (ad) => {
    if (ad) {
      setForm({ title: ad.title, slot: ad.slot, image_url: ad.image_url, link: ad.link, enabled: ad.enabled });
      setEditingId(ad.id);
    } else {
      setForm(EMPTY);
      setEditingId(null);
    }
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) await api.put(`/admin/ads/${editingId}`, form);
      else await api.post("/admin/ads", form);
      toast.success(editingId ? "विज्ञापन अपडेट हो गया" : "विज्ञापन लग गया");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setSaving(false);
  };

  const remove = async (ad) => {
    if (!window.confirm(`"${ad.title || "यह विज्ञापन"}" हटा दें?`)) return;
    try {
      await api.delete(`/admin/ads/${ad.id}`);
      toast.success("विज्ञापन हटा दिया गया");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success("बैनर अपलोड हो गया");
    } catch (err) {
      toast.error(apiError(err));
    }
    setUploading(false);
  };

  const slotLabel = (id) => SLOTS.find((s) => s.id === id)?.label || id;

  return (
    <div className="max-w-3xl space-y-6" data-testid="ads-panel">
      <div className="border border-gold/60 bg-gold-light/20 p-5 flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-inkgray leading-relaxed">
          तीन जगहों पर बैनर दिखते हैं — हीरो के नीचे, पेज के बीच में और फुटर के ऊपर। बैनर न होने पर वहाँ
          "विज्ञापन के लिए संपर्क करें" का संकेत दिखता है।
        </p>
        <button
          data-testid="ad-new-button"
          onClick={() => openForm(null)}
          className="flex items-center gap-2 bg-deepgreen text-paper px-5 py-2.5 text-sm font-semibold hover:bg-deepgreen-hover transition-colors"
        >
          <PlusCircle size={15} /> नया विज्ञापन
        </button>
      </div>

      {showForm && (
        <div className="bg-white border-2 border-ink p-6" data-testid="ad-editor">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
                स्पॉन्सर / ब्रांड का नाम
              </label>
              <input data-testid="ad-title" value={form.title} onChange={set("title")} className={inputCls} placeholder="जैसे: शर्मा ज्वैलर्स, जैसलमेर" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">जगह (Slot)</label>
              <select data-testid="ad-slot-select" value={form.slot} onChange={set("slot")} className={inputCls}>
                {SLOTS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
                बैनर इमेज (चौड़ी इमेज अच्छी दिखेगी)
              </label>
              <input data-testid="ad-image" value={form.image_url} onChange={set("image_url")} className={inputCls} placeholder="https://… या अपलोड करें" />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="ad-image-file" onChange={upload} />
              <div className="flex items-start gap-4 mt-2">
                <button
                  data-testid="ad-image-upload"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 border border-ink/30 px-4 py-2 text-xs font-semibold uppercase tracking-widest hover:border-deepgreen hover:text-deepgreen transition-colors disabled:opacity-60"
                >
                  <ImagePlus size={14} /> {uploading ? "अपलोड हो रहा…" : "कंप्यूटर से बैनर अपलोड करें"}
                </button>
                {form.image_url && (
                  <img src={form.image_url} alt="preview" data-testid="ad-image-preview" className="h-16 max-w-[240px] object-cover border border-ink/20" />
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
                लिंक (क्लिक पर कहाँ जाए)
              </label>
              <input data-testid="ad-link" value={form.link} onChange={set("link")} className={inputCls} placeholder="https://… या https://wa.me/919521018646" />
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input data-testid="ad-enabled" type="checkbox" checked={form.enabled} onChange={set("enabled")} className="w-4 h-4 accent-[#0F382C]" />
              साइट पर दिखाएं
            </label>
          </div>
          <div className="flex gap-3 mt-6">
            <button
              data-testid="ad-save"
              onClick={save}
              disabled={saving || !form.image_url.trim()}
              className="bg-deepgreen text-paper px-6 py-2.5 font-semibold hover:bg-deepgreen-hover transition-colors disabled:opacity-60"
            >
              {saving ? "…" : editingId ? "अपडेट करें" : "लगाएं"}
            </button>
            <button data-testid="ad-cancel" onClick={() => setShowForm(false)} className="border border-ink/30 px-6 py-2.5 font-semibold hover:border-deepgreen transition-colors">
              रद्द करें
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-ink/15" data-testid="ads-list">
        {ads.map((ad) => (
          <div key={ad.id} className="flex items-center gap-4 px-5 py-3.5 border-b border-subtle last:border-0">
            {ad.image_url ? (
              <img src={ad.image_url} alt={ad.title} className="w-24 h-12 object-cover border border-ink/15 shrink-0" />
            ) : (
              <span className="w-24 h-12 bg-subtle flex items-center justify-center shrink-0">
                <Megaphone size={16} className="text-muted-foreground" />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm truncate">{ad.title || "(बिना नाम)"}</p>
              <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
                {slotLabel(ad.slot)} • {ad.clicks} clicks
                {!ad.enabled && <span className="text-brk"> • बंद</span>}
              </p>
            </div>
            <button data-testid={`ad-edit-${ad.id}`} onClick={() => openForm(ad)} className="p-2 text-muted-foreground hover:text-deepgreen transition-colors" aria-label="edit">
              <Pencil size={16} />
            </button>
            <button data-testid={`ad-delete-${ad.id}`} onClick={() => remove(ad)} className="p-2 text-muted-foreground hover:text-brk transition-colors" aria-label="delete">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {ads.length === 0 && (
          <p className="p-10 text-center text-sm text-muted-foreground" data-testid="ads-empty">
            अभी कोई विज्ञापन नहीं — ऊपर "नया विज्ञापन" से पहला बैनर लगाएं
          </p>
        )}
      </div>
    </div>
  );
}
